// /src/utils/api.js

// Use the current domain for API calls, with fallback to localhost for development
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  // Use the current domain for production, localhost for development
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  return isLocalhost ? 'http://localhost:8787/api' : `${window.location.origin}/api`
}

const API_BASE_URL = getApiBaseUrl()

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    }

    // Add auth token if available
    const token = localStorage.getItem('adminToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Network error' }))
        throw new Error(error.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('API Error:', error)
      throw error
    }
  }

  // Plants API
  async getPlants(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.append('category', params.category)
    if (params.search) searchParams.append('search', params.search)
    
    const query = searchParams.toString()
    const endpoint = `/plants${query ? `?${query}` : ''}`
    
    return this.request(endpoint)
  }

  async getPlant(id) {
    return this.request(`/plants?id=${id}`)
  }

  async createPlant(plantData) {
    return this.request('/plants', {
      method: 'POST',
      body: JSON.stringify(plantData)
    })
  }

  async updatePlant(id, plantData) {
    return this.request(`/plants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plantData)
    })
  }

  async deletePlant(id) {
    return this.request(`/plants/${id}`, {
      method: 'DELETE'
    })
  }

  // Admin API
  async adminGetPlants(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.append('category', params.category)
    if (params.search) searchParams.append('search', params.search)
    
    const query = searchParams.toString()
    const endpoint = `/admin/plants${query ? `?${query}` : ''}`
    
    return this.request(endpoint)
  }

  async adminCreatePlant(plantData) {
    return this.request('/admin/plants', {
      method: 'POST',
      body: JSON.stringify(plantData)
    })
  }

  async adminUpdatePlant(id, plantData) {
    return this.request(`/admin/plants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(plantData)
    })
  }

  async adminDeletePlant(id) {
    return this.request(`/admin/plants/${id}`, {
      method: 'DELETE'
    })
  }

  // Reforestation API
  async getReforestationProjects() {
    return this.request('/reforestation')
  }

  async getReforestationProject(id) {
    return this.request(`/reforestation/${id}`)
  }

  // Seeds API
  async getSeeds(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.category) searchParams.append('category', params.category)
    if (params.search) searchParams.append('search', params.search)
    
    const query = searchParams.toString()
    const endpoint = `/seeds${query ? `?${query}` : ''}`
    
    return this.request(endpoint)
  }

  async getSeed(id) {
    return this.request(`/seeds/${id}`)
  }

  // Search API
  async search(query) {
    return this.request(`/search?q=${encodeURIComponent(query)}`)
  }
}

export default new ApiService() 